import dayjs from 'dayjs';

import { DateRange } from '@components/DateRangePicker/DateRangePickerModal';
import {
  DocumentGroup,
  SearchQueryVariables,
  TaskPriority,
  TaskSearchSortOption,
  TaskSortOption,
} from '@graphql/generated';

export type SearchTabName = 'Projects' | 'Tasks' | 'Files' | 'Contacts';

const SEARCH_TAB_NAMES: readonly SearchTabName[] = [
  'Projects',
  'Tasks',
  'Files',
  'Contacts',
];

const isSearchTabName = (tab: string): tab is SearchTabName =>
  SEARCH_TAB_NAMES.some((name) => name === tab);

// tabFocus (from useTabFocus) is a global, app-wide focus context that also
// carries non-search values like 'Chats'/'Teams'/'Activity'/'' when Global
// Search mounts while the user was last on a non-search area of the app.
// Narrow it to one of the 4 real search tabs instead of blindly casting, so
// a stale non-search value can never reach the backend as an invalid `tab`
// query variable (TA-10216).
export const resolveSearchTab = (tab: string): SearchTabName =>
  isSearchTabName(tab) ? tab : 'Projects';

export type ProjectSortStatus = 'active' | 'archived';

export const DEFAULT_PROJECT_SORT_STATUS: ProjectSortStatus = 'active';

const TASK_SEARCH_SORT_VALUES = new Set<string>(
  Object.values(TaskSearchSortOption)
);

export const toTaskSearchSortOption = (
  sort: TaskSortOption
): TaskSearchSortOption | undefined =>
  TASK_SEARCH_SORT_VALUES.has(sort)
    ? (sort as unknown as TaskSearchSortOption)
    : undefined;

export const dueDateStrToDateRange = (
  str: string
): { begin: string | undefined; end: string | undefined } => {
  const today = dayjs().startOf('day');
  if (str === 'Today') {
    return {
      begin: today.toISOString(),
      end: today.endOf('day').toISOString(),
    };
  }
  if (str === 'Tomorrow') {
    const tomorrow = today.add(1, 'day');
    return {
      begin: tomorrow.toISOString(),
      end: tomorrow.endOf('day').toISOString(),
    };
  }
  if (str === 'Next 3 days') {
    return {
      begin: today.toISOString(),
      end: today.add(3, 'day').endOf('day').toISOString(),
    };
  }
  return { begin: undefined, end: undefined };
};

export const computeDueDateParams = (
  dateRange: DateRange,
  strList: string[]
): { begin: string | undefined; end: string | undefined } => {
  if (dateRange.start || dateRange.end) {
    return {
      begin: dateRange.start?.startOf('day').toISOString(),
      end: dateRange.end?.endOf('day').toISOString(),
    };
  }
  if (strList.length > 0) {
    return dueDateStrToDateRange(strList[0]);
  }
  return { begin: undefined, end: undefined };
};

export const getFilesDocumentGroup = (
  filesFilter: string[]
): DocumentGroup | undefined => {
  if (filesFilter.length !== 1) {
    return undefined;
  }
  if (filesFilter[0] === 'Media') {
    return DocumentGroup.Media;
  }
  if (filesFilter[0] === 'Files') {
    return DocumentGroup.File;
  }
  return undefined;
};

type BuildSearchQueryVariablesParams = {
  term: string;
  tab: SearchTabName;
  size: number;
  from: number;
  projectsSortStatus: ProjectSortStatus;
  filesFilter: string[];
  isMyUploads: boolean;
  meId?: string;
  projectOwnerIds: string[];
  projectUserIds: string[];
  teamIds: string[];
  taskUserIds: string[];
  projectIds: string[];
  taskPriority: TaskPriority[];
  taskFilterDuedate: DateRange;
  taskFilterDueDateStrList: string[];
  taskSortList: TaskSortOption[];
};

export const buildSearchQueryVariables = ({
  term,
  tab,
  size,
  from,
  projectsSortStatus,
  filesFilter,
  isMyUploads,
  meId,
  projectOwnerIds,
  projectUserIds,
  teamIds,
  taskUserIds,
  projectIds,
  taskPriority,
  taskFilterDuedate,
  taskFilterDueDateStrList,
  taskSortList,
}: BuildSearchQueryVariablesParams): SearchQueryVariables => {
  const dueDateParams =
    tab === 'Tasks'
      ? computeDueDateParams(taskFilterDuedate, taskFilterDueDateStrList)
      : { begin: undefined, end: undefined };

  return {
    term,
    size,
    from,
    includeMessages: false,
    includeChats: false,
    includeContacts: tab === 'Contacts',
    includeDocuments: tab === 'Files',
    includeProjects: tab === 'Projects',
    includeTasks: tab === 'Tasks',
    archiveProjectsOnly:
      tab === 'Projects' ? projectsSortStatus === 'archived' : undefined,
    includeCompletedTask: true,
    documentOwnerIds:
      tab === 'Files' && isMyUploads && meId ? [meId] : undefined,
    documentGroup:
      tab === 'Files' ? getFilesDocumentGroup(filesFilter) : undefined,
    projectOwnerIds:
      tab === 'Projects' && projectOwnerIds.length > 0
        ? projectOwnerIds
        : undefined,
    projectUserIds:
      tab === 'Projects' && projectUserIds.length > 0
        ? projectUserIds
        : undefined,
    teamIds: tab === 'Projects' && teamIds.length > 0 ? teamIds : undefined,
    taskUserIds:
      tab === 'Tasks' && taskUserIds.length > 0 ? taskUserIds : undefined,
    projectIds:
      tab === 'Tasks' && projectIds.length > 0 ? projectIds : undefined,
    taskPriority:
      tab === 'Tasks' && taskPriority.length > 0 ? taskPriority : undefined,
    taskDueDateBegin: dueDateParams.begin,
    taskDueDateEnd: dueDateParams.end,
    taskSort:
      tab === 'Tasks' && taskSortList.length > 0
        ? toTaskSearchSortOption(taskSortList[0])
        : undefined,
  };
};
